import type { ReactNode } from "react";

import type { SellerProjectFacts, SellerThreadSummary } from "@recruiter/shared";

import { sellerDocUrl } from "../../lib/seller-client";
import { PHASE_META, STAGE_META } from "../../lib/talent-stage";
import {
  ArrowUpRightIcon,
  CalendarIcon,
  ClockIcon,
  DocIcon,
  InboxIcon,
  LayersIcon,
  UsersIcon,
  WalletIcon,
} from "./icons";

interface Fact {
  key: string;
  icon: ReactNode;
  label: string;
  value: string;
}

/** The brief's money, time and shape as the card's fact chips. Budget and timeline always
 *  show (saying what the brief leaves open); the rest only when the brief states them. */
function factChips(f: SellerProjectFacts): Fact[] {
  const facts: Fact[] = [
    {
      key: "budget",
      icon: <WalletIcon size={15} />,
      label: "Budget",
      value: f.budget ?? "Not stated",
    },
    {
      key: "timeline",
      icon: <ClockIcon size={15} />,
      label: "Timeline",
      value: f.timeline ?? "Flexible",
    },
  ];
  if (f.engagement) {
    facts.push({
      key: "shape",
      icon: <LayersIcon size={15} />,
      label: "Engagement",
      value: f.engagement,
    });
  }
  if (f.workload) {
    facts.push({
      key: "load",
      icon: <CalendarIcon size={15} />,
      label: "Workload",
      value: f.workload,
    });
  }
  if (f.headcount) {
    facts.push({
      key: "team",
      icon: <UsersIcon size={15} />,
      label: "Team",
      value: `Hiring ${f.headcount} talents`,
    });
  }
  return facts;
}

// One project on the talent's page: the phase, the name, a short description, the brief's
// facts. The talent's own status shows only when the next move is theirs (a chip beside the
// phase). Two pills, "Read the full brief" and "Open inbox" (which opens the conversation over
// the page from the right), appear on hover or keyboard focus; they stay visible on the card
// that is open, and always on a touch screen, which has no hover. Server state only (a
// thread-list row).
export function ProjectCard({
  thread: t,
  open,
  muted = false,
  onOpen,
  index = 0,
}: {
  thread: SellerThreadSummary;
  /** This project is the one open in the side panel. */
  open: boolean;
  /** A past project: read as secondary. */
  muted?: boolean;
  onOpen: (threadId: string) => void;
  /** Position in the stack, for the entrance stagger. */
  index?: number;
}) {
  const stage = STAGE_META[t.stage];
  const phase = PHASE_META[t.phase];
  const name = t.project_name || "Untitled project";
  return (
    <article
      className="sl-pcard"
      data-open={open}
      data-muted={muted}
      aria-label={name}
      style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
    >
      <div className="sl-pcard__top">
        {stage.needsYou ? (
          <span className={`sl-status sl-status--chip sl-tone--${stage.tone}`}>
            <span className={`sl-dot sl-tone--${stage.tone} sl-dot--ping`} aria-hidden="true" />
            {stage.label}
          </span>
        ) : null}
        <span className={`sl-phase sl-pcard__phase${phase.live ? " sl-phase--live" : ""}`}>
          {phase.live ? <span className="sl-phase__dot" aria-hidden="true" /> : null}
          {phase.label}
        </span>
      </div>
      <h3 className="sl-pcard__title">{name}</h3>
      {t.summary ? <p className="sl-pcard__summary">{t.summary}</p> : null}
      <div className="sl-pcard__row">
        <dl className="sl-facts">
          {factChips(t.facts).map((fact) => (
            <div key={fact.key} className="sl-fact">
              <span className="sl-fact__icon" aria-hidden="true">
                {fact.icon}
              </span>
              <dt className="sl-fact__label">{fact.label}</dt>
              <dd className="sl-fact__value">{fact.value}</dd>
            </div>
          ))}
        </dl>
        <div className="sl-pcard__actions">
          <a
            className="sl-pill-btn sl-pcard__hover"
            href={sellerDocUrl(t.brief_url)}
            target="_blank"
            rel="noreferrer"
          >
            <DocIcon size={15} />
            Read the full brief
            <ArrowUpRightIcon size={14} />
          </a>
          <button
            type="button"
            className="sl-pill-btn sl-pcard__hover sl-pcard__inbox"
            onClick={() => onOpen(t.thread_id)}
            aria-expanded={open}
            aria-controls={open ? "sl-project-panel" : undefined}
          >
            <InboxIcon size={15} />
            {open ? "Inbox open" : "Open inbox"}
          </button>
        </div>
      </div>
    </article>
  );
}
